import api from '../services/authService';

const fetchReviews = async ({
  endpoint,
  page,
  status,
  case_type,
  sort_direction,
  sort_by,
  page_size,
  search = "",
  chip_status = "",
  due_on = "",
  matter_id = "",
  scope = "",
  attorney = "",
}) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      status: status == "ALL" ? "" : status,
      case_type: case_type || "",
      sort_direction: sort_direction || "",
      sort_by: sort_by || "",
      page_size: page_size.toString(),
      search,
      chip_status,
      due_on: due_on == "ALL" ? "" : due_on,
    });
    // Scopes the list to one matter and to everyone working it, for the
    // matter page's activity section. Omitted entirely on Home so that
    // list keeps its existing per-user behaviour.
    if (matter_id) {
      params.append("matter_id", matter_id);
    }
    // Home > Send to Veterans Affairs tabs (My submissions / My matters /
    // Firm-wide). Without this, the server defaults to the caller's own
    // filings even when a wider tab is selected.
    if (scope) {
      params.append("scope", scope);
    }
    if (attorney) {
      params.append("attorney", attorney);
    }

    const response = await api.get(`${endpoint}?${params.toString()}`);
    return response;
  } catch (error) {
    console.error(`Error fetching reviews from ${endpoint}:`, error);
    const errorMessage = error.response?.data?.message || `Failed to fetch reviews from ${endpoint}`;
    const statusCode = error.response?.status || 500;
    const structuredError = {
      message: errorMessage,
      status: statusCode,
      originalError: error
    };
    
    throw structuredError;
  }
};

export const fetchMyReviews = (params) => {
  return fetchReviews({ ...params, endpoint: '/my-reviews/' });
};

export const fetchSharedReviews = (params) => {
  return fetchReviews({ ...params, endpoint: '/shared-for-review/' });
};

/**
 * Thread summaries for Home's Messages tab and the Matter activity card.
 * `chip_status` is the Home filter convention; the messages endpoint calls
 * it `chip` so it is intentionally translated at this boundary.
 */
export const fetchMessageThreads = async ({
  page = 1,
  page_size = 25,
  chip_status = "all",
  search = "",
  matter_id = "",
} = {}) => {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(page_size),
    chip: chip_status,
    search,
  });
  if (matter_id) params.set("matter_id", matter_id);
  return api.get(`/inbox/messages/threads/?${params.toString()}`);
};

/**
 * Cross-document Tasks / Assignments list. Not routed through
 * `fetchReviews` because the parameter sets barely overlap: work items
 * have no case_type, status or due_on, and carry a `role` the review
 * endpoints have no concept of.
 */
const fetchWorkItems = async (endpoint, label, {
  page = 1,
  page_size = 25,
  role = "assigned_to_me",
  chip_status = "ACTIVE",
  search = "",
  matter_id = "",
} = {}) => {
  try {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(page_size),
      chip_status,
      search,
    });
    // On a matter the question is what is outstanding on the case, so the
    // backend ignores `role` and we do not send it — passing both would
    // read as a contradiction to anyone inspecting the request.
    if (matter_id) {
      params.append("matter_id", matter_id);
    } else {
      params.append("role", role);
    }
    return await api.get(`/${endpoint}/?${params.toString()}`);
  } catch (error) {
    console.error(`Error fetching ${label}:`, error);
    throw {
      message:
        error.response?.data?.message || `Failed to fetch ${label}`,
      status: error.response?.status || 500,
      originalError: error,
    };
  }
};

export const fetchAssignments = (params) =>
  fetchWorkItems("tasks", "tasks", params);

export const fetchTasks = (params) =>
  fetchWorkItems("tasks", "tasks", params);

/**
 * Point a Task at the document produced in response to it, or pass a
 * null documentId to unlink. Submitting that document for review is
 * the usual way this gets set; this remains for one-release compatibility.
 */
export const linkAssignmentDeliverable = (assignmentId, documentId) => {
  return api.post(`/tasks/${assignmentId}/link-deliverable/`, {
    document_id: documentId ?? null,
  });
};

export const fetchMySignature = (params) => {
  return fetchReviews({ ...params, endpoint: '/e-signature/widget/needs-my-signature/' });
};

export const fetchSentForSignature = (params) => {
  return fetchReviews({ ...params, endpoint: '/e-signature/widget/sent-for-signature/' });
};

export const fetchCompanySettingsAPI = async () => {
  const response = await api.get(
    `/settings/company/`
  );
  return response;
};

export const updateCompanySettingsAPI = async (payload) => {
  const response = await api.post(
    `/settings/company/`,
    payload,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      },
    }
  );
  return response;
};

export const fetchEmailTemplateSettingsAPI = async (payload) => {
  const response = await api.get(
    `/settings/email-templates/${payload}/`
  );
  return response;
};

export const updateEmailTemplateSettingsAPI = async (payload) => {
  const response = await api.post(
    `/settings/email-templates/${payload.type}/`,
    payload.data
  );
  return response;
};

export const fetchVeteransAffairs = (params) => {
  return fetchReviews({ ...params, endpoint: '/va-uploads/' });
};

export const fetchVaUploadEvidence = (uploadId, scope = 'mine') => {
  return api.get(`/va-uploads/${uploadId}/evidence/`, {
    params: { scope },
  });
};

// Acknowledging is scoped the same way the row was viewed (``scope``/
// ``matter_id``), matching the visibility the acknowledging user already
// had into the row — see VaUploadAcknowledgeErrorView.
export const acknowledgeVaUploadError = (uploadId, { scope = 'mine', matterId } = {}) => {
  return api.post(
    `/va-uploads/${uploadId}/acknowledge-error/`,
    null,
    { params: { scope, ...(matterId ? { matter_id: matterId } : {}) } },
  );
};

export const checkIsFileDeletedAPI = async (payload) => {
  const response = await api.get(
    `/documents/${payload}/is-deleted/`
  );
  return response;
};

// Alert banner (approvals / rework / assignments). Separate from the
// bell's /notifications/ endpoint: the backend restricts this one to a
// hard allowlist of three loud types and caps the payload, so the
// banner cannot be drowned out by fax/routing/reminder traffic.
export const fetchNotificationAlertsAPI = async () => {
  const response = await api.get(`/notifications/alerts/`);
  return response;
};

export const dismissNotificationAlertsAPI = async (payload) => {
  const response = await api.post(`/notifications/alerts/dismiss/`, payload);
  return response;
};

// Review due-date reminder rules (per-user)
export const fetchReviewRemindersAPI = async () => {
  const response = await api.get(`/review-reminders/`);
  return response;
};

export const createReviewReminderAPI = async (payload) => {
  const response = await api.post(`/review-reminders/`, payload);
  return response;
};

export const updateReviewReminderAPI = async ({ id, ...payload }) => {
  const response = await api.patch(`/review-reminders/${id}/`, payload);
  return response;
};

export const deleteReviewReminderAPI = async (id) => {
  const response = await api.delete(`/review-reminders/${id}/`);
  return response;
};
